// Generated by tutorials/record.cjs from tutorials/articles/h61-roles-and-access.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h61 --text-only` (wording) or
// `node tutorials/record.cjs h61` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'roles-and-access',
  catalogId: 'h61',
  title: 'Roles and access',
  summary: 'A role decides which pages people can open and what they can change there. Read a role, change it, make a new one, see who has it, and find out why someone cannot see a page.',
  audience: 'Administrators who decide what each job can see and do',
  area: 'setup',
  learn: [
    'What a role is, and what its levels mean',
    'Change what a role can do',
    'Make a new role from one you already have',
    'See who has a role, and move them to another one',
    'Why someone cannot see a page',
  ],
  keywords: [
    'role',
    'roles',
    'security',
    'security role',
    'security level',
    'permission',
    'permissions',
    'access',
    'access level',
    'no access',
    'view only',
    'add',
    'modify',
    'delete',
    'full access',
    'access restricted',
    'cannot see',
    'can\'t see',
    'missing page',
    'greyed out',
    'new role',
    'copy role',
    'new from existing',
    'role usage',
    'who has',
    'replace role',
    'delete role',
    'quick set',
    'feature access',
    'show all features',
    'new staff',
    'new user',
    'sign in',
    'user',
  ],
  steps: [
    {
      id: 'what',
      part: 'Find your roles',
      heading: 'One role per job',
      text: 'Everyone who signs in to Dietech Online has one security role. The role decides which pages they can open and, on each page, whether they can only look or can also add, change and delete.\n\nMany people share a role: all your cooks can have the Cook role. So when you change a role, you change it for everyone who has it.',
      textOnly: true,
    },
    {
      id: 'list',
      part: 'Find your roles',
      heading: 'Open Roles',
      text: 'Select Setup in the menu, then Roles.\n\nThe list shows every role at your facility, with its description and three of its main levels: Dietech, Purchasing and User. Type in Search roles to find one.',
      media: {
        image: {
          src: '/help/roles-and-access/list.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/roles-and-access/list.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Roles list: Administrator, Cook, Diet Clerk, Dietary Aide, Dietitian, Purchasing and Survey View Only, each with a description and its Dietech, Purchasing and User levels.',
      },
    },
    {
      id: 'open',
      part: 'Read a role',
      heading: 'Open a role',
      text: 'Select the role, then Open (or double-click it). Role Settings opens on General: the role\'s name and notes, and its Main Security Levels.\n\nThe main levels open the big parts of Dietech Online: Dietech (the dietary pages), Purchasing, User and Roles (administration), Misc and Backup.',
      media: {
        image: {
          src: '/help/roles-and-access/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/roles-and-access/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'Role Settings for Dietary Aide on General: Role Name, Notes, and the Main Security Levels Dietech View Only, Purchasing No Access, Roles No Access.',
      },
    },
    {
      id: 'levels',
      part: 'Read a role',
      heading: 'What the levels mean',
      text: 'Most levels use the same scale, and each level includes the ones below it:\n\n0 No Access: the page is not offered at all.\n\n1 View Only: open the page and look.\n\n2 Add: also add new records.\n\n3 Add/Modify: also change records.\n\n4 Add/Modify/Delete: also delete records.\n\n5 Full Access.\n\nWhat a level allows still depends on the page. Facility Settings, for example, can be changed from level 2. A few settings word their own levels, such as Items or Printers, and their lists say exactly what each level does.',
      textOnly: true,
    },
    {
      id: 'sections',
      part: 'Read a role',
      heading: 'Each page has its own level',
      text: 'Under Security on the left, the levels are grouped: Dietech, Clinical, Plan of Care, Purchasing, Admin, Menu / Recipe and Tables / Tools. Select a group to see each page\'s level.\n\nNo Access is shown in grey, so you can see at a glance what the role cannot open.',
      media: {
        image: {
          src: '/help/roles-and-access/sections.webp',
          width: 1280,
          height: 768,
        },
        clip: {
          src: '/help/roles-and-access/sections.mp4',
          width: 1280,
          height: 768,
          durationSec: 2.4,
        },
        alt: 'The Dietech group for Dietary Aide: Resident View Only, Selective Menu Add / Modify, Selective Menu Touch View Only, and several pages at No Access in grey.',
      },
    },
    {
      id: 'edit',
      part: 'Change a role',
      heading: 'Change a level',
      text: 'Select Edit at the top right. Each level is now a list: pick the new level.\n\nHere the dietary aides will take meal orders on Selective Touch, so Selective Menu Touch goes from View Only to Add/Modify.',
      media: {
        image: {
          src: '/help/roles-and-access/edit.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/roles-and-access/edit.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Edit Role for Dietary Aide on the Dietech group: every level is a list, and Selective Menu Touch is now 3 - Add/Modify. Cancel and Save are at the bottom.',
      },
    },
    {
      id: 'quick-set',
      part: 'Change a role',
      heading: 'Set many levels at once',
      text: 'Quick Set, at the bottom of the list on the left, sets every level of the role in one go (Set All Permissions), or every level in one group (Set by Module). Then go back to the groups and adjust the few that differ.',
      media: {
        image: {
          src: '/help/roles-and-access/quick-set.webp',
          width: 1280,
          height: 876,
        },
        alt: 'Quick Set: buttons from 0 - No Access to 6 - User Management under Set All Permissions, and a list per group under Set by Module.',
      },
    },
    {
      id: 'save',
      part: 'Change a role',
      heading: 'Save',
      text: 'Select Save at the bottom of the page. You are back on Role Settings, showing the new level.\n\nEveryone on the role gets the change the next time they sign in.',
      media: {
        image: {
          src: '/help/roles-and-access/save.webp',
          width: 1280,
          height: 768,
        },
        clip: {
          src: '/help/roles-and-access/save.mp4',
          width: 1280,
          height: 768,
          durationSec: 4.6,
        },
        alt: 'Role Settings for Dietary Aide on the Dietech group after saving: Selective Menu Touch now shows Add / Modify.',
      },
    },
    {
      id: 'copy',
      part: 'Make a new role',
      heading: 'Start from a role you have',
      text: 'The quickest way to make a role is from one that is close to it. On Roles, select that role, then More actions (the three dots) and Copy Role.\n\nNew, at the left of the toolbar, starts a blank role instead, with every level at No Access.',
      media: {
        image: {
          src: '/help/roles-and-access/copy.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/roles-and-access/copy.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'The Roles list with Cook selected and the More actions menu open: Copy Role, Replace Role, View Usage, Export as CSV, Export as Excel and Delete Role.',
      },
    },
    {
      id: 'copy-edit',
      part: 'Make a new role',
      heading: 'Name it and change what differs',
      text: 'The copy opens ready to edit, named after the role you copied with (Copy) at the end. Give it its own Role Name and a description, change the levels that differ, and select Save.',
      media: {
        image: {
          src: '/help/roles-and-access/copy-edit.webp',
          width: 1280,
          height: 876,
        },
        clip: {
          src: '/help/roles-and-access/copy-edit.mp4',
          width: 1280,
          height: 876,
          durationSec: 3.2,
        },
        alt: 'Edit Role for the copy: Role Name Lead Cook, Description / Notes, and the Main Security Levels copied from Cook.',
      },
    },
    {
      id: 'usage',
      part: 'Who has a role',
      heading: 'See who has it',
      text: 'On Role Settings, select Usage (on Roles: More actions, View Usage). Role Usage lists everyone on the role, with their title and email, and whether their sign-in is Active, Locked or Disabled.\n\nCheck it before you change a role, so you know who the change reaches.',
      media: {
        image: {
          src: '/help/roles-and-access/usage.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/roles-and-access/usage.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.5,
        },
        alt: 'Role Usage: Cook, 3 users assigned: Carmen Ashby, Wendell Price (Locked) and Owen Pryor, each with Title Cook and an email.',
      },
    },
    {
      id: 'replace',
      part: 'Who has a role',
      heading: 'Move everyone to another role',
      text: 'Replace Role moves everyone on one role to another in one go, for example when two roles are merged. On Roles, select the role they are leaving, then More actions, Replace Role. Pick the Target Role and select Replace.\n\nA role that nobody has any more can be deleted: More actions, Delete Role. Deleting a role that people still have is refused.',
      media: {
        image: {
          src: '/help/roles-and-access/replace.webp',
          width: 708,
          height: 400,
        },
        clip: {
          src: '/help/roles-and-access/replace.mp4',
          width: 708,
          height: 400,
          durationSec: 2,
        },
        alt: 'The Replace Role box: Replace Survey View Only with another role on all assigned users, and the Target Role list.',
      },
    },
    {
      id: 'restricted',
      part: 'Why can\'t I see a page?',
      heading: 'The role does not allow it',
      text: 'The menu only offers the pages a person\'s role allows. If someone opens a page from a link or a bookmark and their role does not allow it, they see Access Restricted instead.\n\nTo give them the page, raise that page\'s level on their role, as above. To see which role you have yourself, select your name at the top right: it says Role.',
      media: {
        image: {
          src: '/help/roles-and-access/restricted.webp',
          width: 1280,
          height: 214,
        },
        alt: 'Access Restricted: You do not have permission to access Nutrition Compliance. Contact your administrator to request access. And a Go to Home button.',
      },
    },
    {
      id: 'features',
      part: 'Why can\'t I see a page?',
      heading: 'Your facility does not use it',
      text: 'Some pages are only offered when your facility uses that part of Dietech Online, whatever the role allows. For example:\n\nSelective Touch, Global Meal Patterns and Forecasting need a facility that uses production.\n\nThe kitchen display needs to be switched on for your facility.\n\nAssessments are left out when your facility does not use assessments.\n\nOrdering pages need your facility to have purchasing.\n\nIf you think your facility should have one of these, contact Dietech support.',
      textOnly: true,
    },
    {
      id: 'feature-access',
      part: 'Why can\'t I see a page?',
      heading: 'Hidden from the menu',
      text: 'Each person can also hide pages they do not use from their own menu. Select your name at the top right, then About Dietech and Show All Features.\n\nFeature Access shows your role and every page it allows, with a check box for each. A page with its box cleared is hidden from your menu but still allowed. Select Show All to bring everything back.',
      media: {
        image: {
          src: '/help/roles-and-access/feature-access.webp',
          width: 944,
          height: 1146,
        },
        alt: 'The Feature Access box: Role: Administrator, the number of pages shown in the menu, Show All and Hide All, and a check box for each section and page.',
      },
    },
    {
      id: 'new-staff',
      part: 'Why can\'t I see a page?',
      heading: 'A new staff member',
      text: 'Sign-ins are set up by Dietech support, not on this page. When someone joins, contact Dietech support with their name, their email address and the role they should have. Contact them as well to move a person to a different role, or to turn off the sign-in of someone who has left.',
      textOnly: true,
    },
  ],
  tips: [
    'Give each job its own role (Cook, Dietary Aide, Diet Clerk) rather than one role per person. When a job changes, you change one role and everyone in that job follows.',
    'Use Description / Notes to say who the role is for. It shows on the Roles list, so the right role is easy to pick.',
    'Copying a role and changing what differs is quicker and safer than starting a New role, where every level starts at No Access.',
  ],
  notes: [
    'Sign-ins for your staff are set up by Dietech support. To add a new staff member, change the role a person has, or turn off the sign-in of someone who has left, contact Dietech support and say which role they should have.',
    'You can give a role at most the access your own role has. A level higher than your own is refused when you save.',
    'Working with roles needs the Roles level (under Admin, Roles Management): 1 to look at roles, 2 to add or copy one, 3 to change one, 4 to replace or delete one.',
    'People on a role see a change to it the next time they sign in.',
  ],
  related: ['getting-around', 'settings-and-preferences', 'facility-setup', 'glossary'],
  recorded: '2026-11-03',
};

export default article;
